import axios from "axios";
import { server } from "../../server";
import {
  getStoredCurrentProfile,
  buildAiMemberProfileKey,
  isAiMemberProfile,
  isShopProfile,
  setStoredCurrentProfile,
} from "../../utils/shopProfile";
import {
  cacheAuthUser,
  cacheUserShops,
  clearAuthSession,
  persistAuthToken,
} from "../../utils/authSession";

export const SET_SELLER_ON_LOGIN = "SET_SELLER_ON_LOGIN";
export const SET_ACTIVE_SELLER_PROFILE = 'SET_ACTIVE_SELLER_PROFILE';
export const LOAD_SELLERS_SUCCESS = 'LOAD_SELLERS_SUCCESS';
export const SET_ACTIVE_SELLER = 'SET_ACTIVE_SELLER';
export const LOAD_AI_MEMBERS_SUCCESS = 'LOAD_AI_MEMBERS_SUCCESS';
export const LOAD_AI_MEMBERS_FAIL = 'LOAD_AI_MEMBERS_FAIL';
export const SET_ACTIVE_AI_MEMBER = 'SET_ACTIVE_AI_MEMBER';

export const LOAD_ALL_SELLERS_REQUEST = 'LOAD_ALL_SELLERS_REQUEST';
export const LOAD_ALL_SELLERS_SUCCESS = 'LOAD_ALL_SELLERS_SUCCESS';
export const LOAD_ALL_SELLERS_FAIL = 'LOAD_ALL_SELLERS_FAIL';


export const SET_USER_LOCATION = 'SET_USER_LOCATION';
export const LOCATION_ERROR = 'LOCATION_ERROR';
export const SAVE_LOCATION_SUCCESS = 'SAVE_LOCATION_SUCCESS';
export const SAVE_LOCATION_ERROR = 'SAVE_LOCATION_ERROR';

export const FETCH_USER_AND_SUBSCRIPTION_REQUEST = 'FETCH_USER_AND_SUBSCRIPTION_REQUEST';
export const FETCH_USER_AND_SUBSCRIPTION_SUCCESS = 'FETCH_USER_AND_SUBSCRIPTION_SUCCESS';
export const FETCH_USER_AND_SUBSCRIPTION_FAILURE = 'FETCH_USER_AND_SUBSCRIPTION_FAILURE';

export const FETCH_SHOP_AND_SUBSCRIPTIONS_REQUEST = 'FETCH_SHOP_AND_SUBSCRIPTIONS_REQUEST';
export const FETCH_SHOP_AND_SUBSCRIPTIONS_SUCCESS = 'FETCH_SHOP_AND_SUBSCRIPTIONS_SUCCESS';
export const FETCH_SHOP_AND_SUBSCRIPTIONS_FAILURE = 'FETCH_SHOP_AND_SUBSCRIPTIONS_FAILURE';

// Set user
export const setUser = (user) => {
  return {
    type: "SetUser",
    payload: user,
  };
};

// set seller on login
export const setSellerOnLogin = (seller) => {
  return {
    type: SET_SELLER_ON_LOGIN,
    payload: seller,
  };
};

// load user
export const loadUser = () => async (dispatch) => {
  try {
    dispatch({
      type: "LoadUserRequest",
    });
    const { data } = await axios.get(`${server}/user/getuser`, { withCredentials: true });
    if (data?.token || data?.user?.token) {
      persistAuthToken(data.token || data.user.token);
    }
    if (data?.user) {
      cacheAuthUser(data.user);
    }
    dispatch({
      type: "LoadUserSuccess",
      payload: data.user,
    });
    dispatch(loadSellers());
    dispatch(loadAiMembers());
  } catch (error) {
    const statusCode = error?.response?.status;
    const isAuthFailure = statusCode === 401 || statusCode === 403;

    if (isAuthFailure) {
      clearAuthSession();
      setStoredCurrentProfile("User");
    }

    dispatch({
      type: "LoadUserFail",
      payload: {
        message: error?.response?.data?.message || "There was a problem loading the user data. Please try again.",
        isAuthFailure,
      },
    });
  }
};

export const loadAiMembers = () => async (dispatch) => {
  try {
    const { data } = await axios.get(`${server}/ai-member/mine`, { withCredentials: true });
    dispatch({ type: LOAD_AI_MEMBERS_SUCCESS, payload: data.aiMembers || [] });
  } catch (error) {
    dispatch({
      type: LOAD_AI_MEMBERS_FAIL,
      payload: error.response?.data?.message || "There was a problem loading AI Member profiles.",
    });
  }
};

export const setActiveAiMember = (aiMember) => (dispatch) => {
  const profileKey = buildAiMemberProfileKey(aiMember);
  if (!profileKey) return;

  setStoredCurrentProfile(profileKey);
  dispatch({ type: SET_ACTIVE_AI_MEMBER, payload: aiMember });
  dispatch({ type: "SwitchProfile", payload: profileKey });
};

// Load sellers
export const loadSellers = () => async (dispatch) => {
  try {
    
    dispatch({ type: LOAD_ALL_SELLERS_REQUEST });
    const { data } = await axios.get(`${server}/user/my-shops`, { withCredentials: true });
    cacheUserShops(data.shops || []);
    
    dispatch({ type: LOAD_SELLERS_SUCCESS, payload: data.shops || [] });
  } catch (error) {
    console.log('Loading sellers: Error', error.response?.data?.message || "There was a problem loading the user's shops.");
    dispatch({ type: LOAD_ALL_SELLERS_FAIL , payload: error.response?.data?.message || "There was a problem loading the user's shops." });
  }
};


export const loadAllSellers = (brand) => async (dispatch) => {
  try {
    dispatch({ type: LOAD_ALL_SELLERS_REQUEST });
    const endpoint = brand ? `${server}/shop/all-sellers?brand=${brand}` : `${server}/shop/all-sellers`;
    const { data } = await axios.get(endpoint);
    dispatch({ type: LOAD_ALL_SELLERS_SUCCESS, payload: data.allSellers });
  } catch (error) {
    dispatch({
      type: LOAD_ALL_SELLERS_FAIL,
      payload: error.response?.data?.message || "There was a problem loading the sellers."
    });
  }
};

export const setActiveSeller = (seller) => (dispatch) => {
  if (!seller) {
    return;
  }

  setStoredCurrentProfile(seller.name);
  dispatch({
    type: SET_ACTIVE_SELLER,
    payload: seller,
  });
  dispatch({
    type: "SwitchProfile",
    payload: seller.name,
  });
  dispatch(loadSeller(seller.name));
};


// load seller
export const loadSeller = (currentProfileFromSession) => async (dispatch) => {
  try {
    dispatch({
      type: "LoadSellerRequest",
    });

    const currentProfile = currentProfileFromSession || getStoredCurrentProfile();

    if (!currentProfile) {
      dispatch({
        type: "ClearSellerState",
      });
      return;
    }

    if (!isShopProfile(currentProfile)) {
      dispatch({
        type: "ClearSellerState",
      });
      return;
    }

    const { data } = await axios.get(`${server}/shop/getSeller`, {
      withCredentials: true,
      headers: {
        'Current-Profile': currentProfile,
      },
    });

    dispatch({
      type: "LoadSellerSuccess",
      payload: data.seller,
    });
    dispatch({
      type: SET_ACTIVE_SELLER,
      payload: data.seller,
    });
  } catch (error) {
    dispatch({
      type: "LoadSellerFail",
      payload: error?.response?.data?.message || "There was a problem loading the shop profile.",
    });
  }
};

export const fetchUserAndSubscription = () => async (dispatch) => {
  dispatch({ type: FETCH_USER_AND_SUBSCRIPTION_REQUEST });
  

  try {
    const userResponse = await axios.get(`${server}/user/getuser`, { withCredentials: true });
    

    const subscriptionResponse = await axios.get(`${server}/subscriptions/my-subscriptions`, { withCredentials: true });
    

    dispatch({
      type: FETCH_USER_AND_SUBSCRIPTION_SUCCESS,
      payload: {
        user: userResponse.data.user,
        subscriptions: subscriptionResponse.data,
      },
    });
    
  } catch (error) {
    console.error("Failed to fetch user and subscription data:", error.response ? error.response.data.message : error.message);
    dispatch({
      type: FETCH_USER_AND_SUBSCRIPTION_FAILURE,
      payload: error.response ? error.response.data.message : error.message,
    });
  }
};

export const fetchShopAndSubscriptions = (shopId, currentProfileFromSession) => async (dispatch) => {
  dispatch({ type: FETCH_SHOP_AND_SUBSCRIPTIONS_REQUEST });
  
  const currentProfile = currentProfileFromSession || getStoredCurrentProfile();

  if (!isShopProfile(currentProfile)) {
    dispatch({
      type: FETCH_SHOP_AND_SUBSCRIPTIONS_FAILURE,
      payload: "Valid shop profile required to fetch shop data.",
    });
    return;
  }

  try {
      // Fetch shop details
      const shopResponse = await axios.get(`${server}/shop/getSeller`, { withCredentials: true, headers: {
        'Current-Profile': currentProfile, 
      }, });
      

      // Fetch shop subscriptions
      const subscriptionResponse = await axios.get(`${server}/subscriptions/shop-subscriptions/my-subscriptions`, { withCredentials: true,
        headers: {
        'Current-Profile': currentProfile, 
      },});
      

      dispatch({
          type: FETCH_SHOP_AND_SUBSCRIPTIONS_SUCCESS,
          payload: {
              shop: shopResponse.data, 
              subscriptions: subscriptionResponse.data, 
          },
      });
      
  } catch (error) {
      console.error("Failed to fetch shop and subscription data:", error.response ? error.response.data.message : error.message);
      dispatch({
          type: FETCH_SHOP_AND_SUBSCRIPTIONS_FAILURE,
          payload: error.response ? error.response.data.message : error.message,
      });
  }
};

// Switch profile
export const switchProfile = (targetProfileName) => (dispatch, getState) => {
  const currentState = getState();
  const currentProfile = currentState.user.currentProfile;
  const activeSellerName = currentState.seller.activeSeller?.name;

  // Determine the new profile based on the current profile
  let newProfile = targetProfileName;
  if (!newProfile) {
    if (currentProfile === 'User') {
      newProfile = activeSellerName || "User";
    } else {
      newProfile = "User";
    }
  }

  console.log("New profile to switch:", newProfile);

  setStoredCurrentProfile(newProfile);
  dispatch({ type: "SwitchProfile", payload: newProfile });

  // Dispatch actions to load the appropriate data
  if (newProfile === "User") {
    dispatch(loadUser());
  } else if (isAiMemberProfile(newProfile)) {
    const aiMemberId = newProfile.slice("AI Member:".length);
    const aiMember = currentState.user.aiMembers.find((profile) => String(profile._id || profile.id) === aiMemberId);
    dispatch({ type: SET_ACTIVE_AI_MEMBER, payload: aiMember || null });
  } else if (newProfile === activeSellerName) {
    dispatch(loadSeller(newProfile));
  } else if (isShopProfile(newProfile)) {
    dispatch(loadSeller(newProfile));
  }
};





// Add this action creator function in your actions file
export const setActiveSellerProfile = (index) => {
  return {
    type: SET_ACTIVE_SELLER_PROFILE,
    payload: index,
  };
};




// user update information
export const updateUserInformation =
  (name, email, phoneNumber, password) => async (dispatch) => {
    try {
      dispatch({
        type: "updateUserInfoRequest",
      });

      const { data } = await axios.put(
        `${server}/user/update-user-info`,
        {
          email,
          password,
          phoneNumber,
          name,
        },
        {
          withCredentials: true,
          headers: {
            "Access-Control-Allow-Credentials": true,
          },
        }
      );

      dispatch({
        type: "updateUserInfoSuccess",
        payload: data.user,
      });
    } catch (error) {
      dispatch({
        type: "updateUserInfoFailed",
        payload: "There was a problem updating the user data. Please try again.",
      });
    }
  };

// update user address
export const updatUserAddress =
  (country, city, address1, address2, zipCode, addressType) =>
  async (dispatch) => {
    try {
      dispatch({
        type: "updateUserAddressRequest",
      });

      const { data } = await axios.put(
        `${server}/user/update-user-addresses`,
        {
          country,
          city,
          address1,
          address2,
          zipCode,
          addressType,
        },
        { withCredentials: true }
      );

      dispatch({
        type: "updateUserAddressSuccess",
        payload: {
          successMessage: "User address updated succesfully!",
          user: data.user,
        },
      });
    } catch (error) {
      dispatch({
        type: "updateUserAddressFailed",
        payload: "There was a problem updating the user address data. Please try again.",
      });
    }
  };

// delete user address
export const deleteUserAddress = (id) => async (dispatch) => {
  try {
    dispatch({
      type: "deleteUserAddressRequest",
    });

    const { data } = await axios.delete(
      `${server}/user/delete-user-address/${id}`,
      { withCredentials: true }
    );

    dispatch({
      type: "deleteUserAddressSuccess",
      payload: {
        successMessage: "User deleted successfully!",
        user: data.user,
      },
    });
  } catch (error) {
    dispatch({
      type: "deleteUserAddressFailed",
      payload: "There was a problem deleting the user address. Please try again.",
    });
  }
};

// get all users --- admin
export const getAllUsers = () => async (dispatch) => {
  try {
    dispatch({
      type: "getAllUsersRequest",
    });

    const { data } = await axios.get(`${server}/user/admin-all-users`, {
      withCredentials: true,
    });

    dispatch({
      type: "getAllUsersSuccess",
      payload: data.users,
    });
  } catch (error) {
    dispatch({
      type: "getAllUsersFailed",
      payload: "There was a problem loading the user data. Please try again.",
    });
  }
};

//Save User Location
export const saveLocation = (locationData) => async (dispatch) => {
  try {
      const response = await axios({
          method: 'post',
          url: `${server}/locations/locations`,
          data: locationData,
          withCredentials: true, 
          headers: {
              'Content-Type': 'application/json'
          }
      });
      dispatch({
          type: SAVE_LOCATION_SUCCESS,
          payload: response.data 
      });
  } catch (error) {
      dispatch({
          type: SAVE_LOCATION_ERROR,
          payload: error.response ? error.response.data.message : error.message
      });
  }
};

export const setUserLocation = (location) => ({
  type: SET_USER_LOCATION,
  payload: location,
});

export const locationError = (error) => ({
  type: LOCATION_ERROR,
  payload: error,
});